import React, { useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, IconButton, Typography } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import FiberNewIcon from "@material-ui/icons/FiberNew";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import BuildIcon from "@material-ui/icons/Build";
import WarningIcon from "@material-ui/icons/Warning";
import NotesIcon from "@material-ui/icons/Notes";
import CloseIcon from "@material-ui/icons/Close";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import { ChangeKind, ChangelogItem, ChangelogVersion } from "./changelog";

/** Colour, icon and labels of each kind of change, shared by the sidebar counters */
export const KIND_STYLE: Record<ChangeKind, {
  color: string; background: string; Icon: React.ElementType; label: string; one: string; many: string
}> = {
  added: { color: "#1b5fa8", background: "#e5eef9", Icon: FiberNewIcon, label: "Novo", one: "novidade", many: "novidades" },
  improved: { color: "#9a5300", background: "#fbefdc", Icon: TrendingUpIcon, label: "Melhorado", one: "melhoria", many: "melhorias" },
  fixed: { color: "#17784a", background: "#e1f3e9", Icon: BuildIcon, label: "Corrigido", one: "correção", many: "correções" },
  breaking: {
    color: "#b42318", background: "#fdecea", Icon: WarningIcon, label: "Importante", one: "mudança importante", many: "mudanças importantes"
  },
  other: { color: "#5b6878", background: "#eceff3", Icon: NotesIcon, label: "Outro", one: "item", many: "itens" }
};

/** Longest summary shown in the table before the text is cut */
const SUMMARY_LENGTH = 120;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      flexWrap: "wrap",
      marginBottom: theme.spacing(2.5)
    },
    title: {
      fontSize: 26,
      fontWeight: 600,
      lineHeight: 1.2
    },
    date: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      marginTop: 4
    },
    kpis: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(3)
    },
    kpi: {
      display: "grid",
      gridTemplateColumns: "auto 1fr",
      columnGap: theme.spacing(1.5),
      alignItems: "center",
      textAlign: "left",
      font: "inherit",
      cursor: "pointer",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderTopWidth: 4,
      borderRadius: 10,
      padding: theme.spacing(1.5, 2),
      boxShadow: "0 1px 2px rgba(0,0,0,.04)",
      transition: "border-color .15s, transform .15s",
      "&:hover": {
        transform: "translateY(-1px)"
      }
    },
    kpiIcon: {
      gridRow: "1 / 3",
      width: 40,
      height: 40,
      borderRadius: 10,
      display: "grid",
      placeItems: "center",
      "& svg": {
        fontSize: 22
      }
    },
    kpiNumber: {
      fontSize: 28,
      fontWeight: 700,
      lineHeight: 1.1
    },
    kpiLabel: {
      fontSize: 13,
      color: theme.palette.text.secondary
    },
    actions: {
      display: "flex",
      gap: theme.spacing(1),
      flexWrap: "wrap"
    },
    section: {
      marginBottom: theme.spacing(3)
    },
    sectionTitle: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      fontSize: 16,
      fontWeight: 600,
      marginBottom: theme.spacing(1.25)
    },
    sectionIcon: {
      width: 30,
      height: 30,
      borderRadius: 8,
      display: "grid",
      placeItems: "center",
      "& svg": {
        fontSize: 18
      }
    },
    sectionCount: {
      fontSize: 13,
      fontWeight: 400,
      color: theme.palette.text.secondary
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      overflow: "hidden",
      fontSize: 14
    },
    th: {
      textAlign: "left",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: ".06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      padding: theme.spacing(1, 1.5),
      backgroundColor: theme.palette.action.hover,
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    row: {
      cursor: "pointer",
      "&:hover td": {
        backgroundColor: theme.palette.action.hover
      },
      "&:last-child td": {
        borderBottom: 0
      }
    },
    td: {
      padding: theme.spacing(1.25, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: "middle"
    },
    kindChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      borderRadius: 14,
      padding: "2px 9px",
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap",
      "& svg": {
        fontSize: 14
      }
    },
    itemTitle: {
      fontWeight: 600
    },
    itemSummary: {
      display: "block",
      fontSize: 13.5,
      color: theme.palette.text.secondary,
      marginTop: 2
    },
    detailsButton: {
      textTransform: "none",
      whiteSpace: "nowrap"
    },
    dialogHead: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
      padding: theme.spacing(2, 1, 1, 3)
    },
    dialogTitle: {
      flex: 1,
      fontSize: 18,
      fontWeight: 600,
      lineHeight: 1.3,
      marginTop: theme.spacing(1)
    },
    rich: {
      "& code": {
        fontSize: 13,
        padding: "1px 4px",
        borderRadius: 4,
        backgroundColor: theme.palette.action.hover
      }
    },
    lead: {
      fontSize: 15.5,
      lineHeight: 1.55,
      padding: theme.spacing(1.5, 1.75),
      borderRadius: 8,
      borderLeft: "4px solid"
    },
    points: {
      listStyle: "none",
      margin: theme.spacing(2, 0, 0),
      padding: 0,
      fontSize: 14.5,
      lineHeight: 1.55,
      "& li": {
        display: "flex",
        gap: theme.spacing(1.25),
        alignItems: "flex-start"
      },
      "& li + li": {
        marginTop: theme.spacing(1)
      },
      "& li svg": {
        fontSize: 19,
        marginTop: 1,
        flex: "none"
      }
    },
    steps: {
      display: "grid",
      gap: theme.spacing(1),
      marginTop: theme.spacing(2)
    },
    step: {
      display: "flex",
      gap: theme.spacing(1.25),
      alignItems: "flex-start",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      padding: theme.spacing(1.25, 1.5),
      fontSize: 14,
      lineHeight: 1.5
    },
    stepNumber: {
      width: 22,
      height: 22,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      fontSize: 12,
      fontWeight: 700,
      flex: "none"
    }
  })
);

/**
 * Splits a text into sentences without breaking version numbers ("1.107.0") or abbreviations:
 * a new sentence starts only after a full stop followed by a capital letter.
 * @param text Full text of a CHANGELOG item
 */
export function splitSentences(text: string): string[] {
  return text.split(/(?<=[.!?])\s+(?=[A-ZÁÉÍÓÚÂÊÔÃÕÇ*`“"(])/).filter(Boolean);
}

/**
 * Renders the inline markdown the CHANGELOG uses (**bold**, *italic*, `code`) as React nodes,
 * without injecting HTML.
 * @param text One bullet of the CHANGELOG
 */
export function renderInline(text: string): React.ReactNode[] {
  let offset = 0;

  return text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g).filter(Boolean).map((part) => {
    const key = `${offset}`;

    offset += part.length;

    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) return <strong key={key}>{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) return <em key={key}>{part.slice(1, -1)}</em>;
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) return <code key={key}>{part.slice(1, -1)}</code>;

    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

/**
 * Short version of an item for the table: its first sentence, cut at a word when still too long.
 * @param text Full text of the item
 */
export function summarize(text: string): string {
  const plain = text.replace(/\*\*|`/g, "").replace(/\*/g, "");
  const first = plain.split(/(?<=[.!?])\s+/)[0];

  if (first.length <= SUMMARY_LENGTH) return first;

  const cut = first.slice(0, SUMMARY_LENGTH);

  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
}

interface Row {
  kind: ChangeKind;
  item: ChangelogItem;
}

interface ChangelogVersionViewProps {
  entry: ChangelogVersion;
  /** Buttons shown next to the version title (Novidades em detalhe, Resumo de versões) */
  actions?: React.ReactNode;
}

/** One CHANGELOG version as one compact table per kind of change; each row opens a small dialog with the full text */
const ChangelogVersionView: React.FC<ChangelogVersionViewProps> = ({ entry, actions }) => {
  const classes = useStyles();
  const [open, setOpen] = useState<Row | null>(null);
  const rows: Row[] = entry.sections.flatMap((section) => section.items.map((item) => ({ kind: section.kind, item })));
  const kinds = (Object.keys(KIND_STYLE) as ChangeKind[]).filter((kind) => rows.some((row) => row.kind === kind));
  const kindChip = (kind: ChangeKind) => {
    const { color, background, Icon, label } = KIND_STYLE[kind];

    return <span className={classes.kindChip} style={{ color, backgroundColor: background }}><Icon />{label}</span>;
  };

  return (
    <div>
      <div className={classes.header}>
        <div>
          <Typography component="h1" className={classes.title}>{`Versão ${entry.version}`}</Typography>
          {entry.date && <div className={classes.date}>{entry.date}</div>}
        </div>
        {actions && <div className={classes.actions}>{actions}</div>}
      </div>

      <div className={classes.kpis}>
        {kinds.map((kind) => {
          const { color, background, Icon, one, many } = KIND_STYLE[kind];
          const count = rows.filter((row) => row.kind === kind).length;

          return (
            <button
              key={kind}
              type="button"
              className={classes.kpi}
              style={{ borderTopColor: color }}
              onClick={() => document.getElementById(`changelog-${kind}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              <span className={classes.kpiIcon} style={{ color, backgroundColor: background }}><Icon /></span>
              <span className={classes.kpiNumber} style={{ color }}>{count}</span>
              <span className={classes.kpiLabel}>{count === 1 ? one : many}</span>
            </button>
          );
        })}
      </div>

      {entry.sections.filter((section) => section.items.length > 0).map((section) => {
        const { color, background, Icon } = KIND_STYLE[section.kind];

        return (
          <section key={section.title} id={`changelog-${section.kind}`} className={classes.section}>
            <Typography component="h2" className={classes.sectionTitle}>
              <span className={classes.sectionIcon} style={{ color, backgroundColor: background }}><Icon /></span>
              {section.title}
              <span className={classes.sectionCount}>{section.items.length}</span>
            </Typography>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.th}>O que mudou</th>
                  <th className={classes.th} style={{ width: 110 }} aria-label="Detalhes" />
                </tr>
              </thead>
              <tbody>
                {section.items.map((item) => ({ kind: section.kind, item })).map((row) => (
                  <tr key={`${row.item.title}-${row.item.text.slice(0, 30)}`} className={classes.row} onClick={() => setOpen(row)}>
                    <td className={classes.td} style={{ borderLeft: `3px solid ${color}` }}>
                      {row.item.title && <span className={classes.itemTitle}>{renderInline(row.item.title)}</span>}
                      <span className={classes.itemSummary}>{summarize(row.item.text)}</span>
                    </td>
                    <td className={classes.td} style={{ textAlign: "right" }}>
                      <Button
                        size="small"
                        color="primary"
                        className={classes.detailsButton}
                        endIcon={<ChevronRightIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen(row);
                        }}
                      >
                  Detalhes
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="sm" fullWidth aria-labelledby="changelog-item-title">
        {open && (
          <>
            <div className={classes.dialogHead}>
              <div style={{ flex: 1 }}>
                {kindChip(open.kind)}
                <Typography id="changelog-item-title" className={classes.dialogTitle}>
                  {open.item.title ? renderInline(open.item.title) : `Versão ${entry.version}`}
                </Typography>
              </div>
              <IconButton aria-label="Fechar" onClick={() => setOpen(null)}>
                <CloseIcon />
              </IconButton>
            </div>
            <DialogContent className={classes.rich}>
              {(() => {
                // long texts read better split: the first sentence as the lead, the rest as checked points
                const { color, background } = KIND_STYLE[open.kind];
                const [lead, ...points] = splitSentences(open.item.text);

                return (
                  <>
                    <div className={classes.lead} style={{ backgroundColor: background, borderLeftColor: color }}>{renderInline(lead || "")}</div>
                    {points.length > 0 && (
                      <ul className={classes.points}>
                        {points.map((point) => (
                          <li key={point.slice(0, 60)}>
                            <CheckCircleOutlineIcon style={{ color }} />
                            <span>{renderInline(point)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {open.item.details.length > 0 && (
                      <div className={classes.steps}>
                        {open.item.details.map((detail, position) => (
                          <div key={detail.slice(0, 60)} className={classes.step}>
                            <span className={classes.stepNumber} style={{ color, backgroundColor: background }}>{position + 1}</span>
                            <span>{renderInline(detail)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Typography variant="caption" color="textSecondary" style={{ flex: 1, paddingLeft: 16 }}>
                {`Versão ${entry.version}${entry.date ? ` · ${entry.date}` : ""}`}
              </Typography>
              <Button color="primary" onClick={() => setOpen(null)}>Fechar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ChangelogVersionView;
